import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { getDisplayPlanName } from "@/lib/planNames";
import "@/styles/account.css";

export default function BillingPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleManageBilling = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/stripe/portal", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert(data.error || "Failed to open billing portal");
      }
    } catch {
      alert("Failed to open billing portal. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (iso: string | null | undefined) => {
    if (!iso) return null;
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const currentPlan = user?.plan || "starter";
  const displayName = getDisplayPlanName(currentPlan, user?.subscription_status, user?.email);
  const isPaid = currentPlan !== "starter" && user?.subscription_status && !["canceled"].includes(user.subscription_status);

  return (
    <div className="card simple-page-card">
      <h2>Billing</h2>

      <div className="info-box">
        <div className="info-box-label">Current Plan</div>
        <div className="info-box-value">{displayName}</div>
        {user?.subscription_status && (
          <div className="info-box-desc">
            Status: <span className={`billing-status billing-status-${user.subscription_status}`}>
              {user.subscription_status}
            </span>
          </div>
        )}
        {user?.current_period_end && (
          <div className="info-box-desc">
            {user.cancel_at_period_end
              ? `Access until ${formatDate(user.current_period_end)}`
              : user.subscription_status === "trialing"
              ? `Trial ends ${formatDate(user.current_period_end)}`
              : `Renews on ${formatDate(user.current_period_end)}`
            }
          </div>
        )}
        {user?.cancel_at_period_end && (
          <div className="info-box-desc billing-cancel-notice">
            Your subscription is set to cancel at the end of the billing period.
          </div>
        )}
      </div>

      {isPaid && user?.has_stripe ? (
        <div className="billing-actions">
          <button
            className="btn btn-secondary"
            onClick={handleManageBilling}
            disabled={loading}
          >
            {loading ? "Opening..." : "Manage Billing"}
          </button>
          <p className="billing-manage-desc">
            Update payment method, change plan, view invoices, or cancel.
          </p>
        </div>
      ) : isPaid ? (
        <div className="billing-actions">
          <div className="info-box-desc">
            Your plan was activated internally. No billing management needed.
          </div>
        </div>
      ) : (
        <div className="billing-actions">
          <p className="billing-upgrade-text">
            Subscribe to unlock full MCP access, API keys, and more.
          </p>
          <Link to="/pricing" className="btn btn-primary">
            View Plans
          </Link>
        </div>
      )}
    </div>
  );
}
